import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { DataGrid, GridCellParams, GridColDef } from '@mui/x-data-grid'
import { UserRole } from '../../types/keycloak'
import { RootState, useAppDispatch } from '../../redux/store'
import {
  activateUserByUsername,
  fetchUsers,
} from '../../redux/users/usersAction'
import {
  selectUsers,
  selectUsersLoading,
} from '../../redux/users/usersSelectors'
import { User } from '../../types/userTypes'
import ConfirmDialog from '../../components/containers/ConfirmDialog'
import { hasRole } from '../../utils/checkRole'
import UseAlert from '../../components/common/alert'
import { formatTitle } from '../../utils/formatTitle'
import { useTableLoadingGate } from '../../hooks/useTableLoadingGate'
import { ListPagePill } from '../../components/common/ListPageCells'
import { ListPageDescription } from '../../components/common/ListPageDescription'
import {
  LIST_PAGE_COLORS,
  ListPageShell,
  listPageChipSx,
  listPageDataGridSx,
  listPageGridSlots,
} from '../../components/common/ListPageShell'
import '../../components/common/ListPageTable.css'

type StatusFilter = 'all' | 'enabled' | 'disabled'

type TeamRow = {
  id: string
  username: string
  email: string
  clientRoles: string[]
  enabled: boolean
}

function initials(username: string) {
  const parts = username.split(/[.\s_-]+/).filter(Boolean)
  const letters = (parts[0]?.[0] || '') + (parts[1]?.[0] || '')
  return (letters || username.slice(0, 2)).toUpperCase()
}

const TeamPage = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const users: User[] = useSelector((state: RootState) => selectUsers(state))
  const loading = useSelector(selectUsersLoading)
  const showLoader = useTableLoadingGate(loading)
  const roles = useSelector((state: RootState) => state.auth.roles)
  const isRSSI = hasRole(roles, UserRole.RSSI)
  const isAdmin = hasRole(roles, UserRole.ADMIN)
  const canManage = isAdmin || isRSSI

  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [selectedUsername, setSelectedUsername] = useState<string | null>(null)
  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: '' as 'info' | 'success' | 'error' | 'warning',
  })

  React.useEffect(() => {
    dispatch(fetchUsers())
  }, [dispatch])

  const roleLabel = (role?: string) => {
    if (!role) return t('teamPage.noRole')
    return t(`teamPage.roles.${role}`, { defaultValue: role })
  }

  const rows = useMemo(() => {
    const query = search.trim().toLowerCase()
    return users
      .filter((user) => {
        if (!user.enabled && !canManage) return false
        if (statusFilter === 'enabled' && !user.enabled) return false
        if (statusFilter === 'disabled' && user.enabled) return false
        if (!query) return true
        return (
          user.username.toLowerCase().includes(query) ||
          (user.email || '').toLowerCase().includes(query)
        )
      })
      .map(
        (user): TeamRow => ({
          id: user.id || user.username,
          username: user.username,
          email: user.email,
          clientRoles: user.clientRoles || [],
          enabled: user.enabled,
        })
      )
      .sort((a, b) => {
        if (a.enabled !== b.enabled) return a.enabled ? 1 : -1
        return a.username.localeCompare(b.username)
      })
  }, [users, search, statusFilter, canManage])

  const selected = rows.find((row) => row.username === selectedUsername) ||
    users.find((user) => user.username === selectedUsername)

  const handleEnableClick = (username: string) => {
    setSelectedUsername(username)
    setConfirmOpen(true)
  }

  const handleConfirmEnable = async () => {
    if (!selectedUsername) return
    try {
      const result = await dispatch(activateUserByUsername(selectedUsername))
      if (activateUserByUsername.fulfilled.match(result)) {
        setAlert({
          open: true,
          message: t('alerts.activateUser.success'),
          severity: 'success',
        })
      } else {
        throw new Error(result.error.message || 'Activation failed')
      }
    } catch {
      setAlert({
        open: true,
        message: t('alerts.activateUser.error'),
        severity: 'error',
      })
    } finally {
      setConfirmOpen(false)
      setSelectedUsername(null)
    }
  }

  const columns: GridColDef[] = [
    {
      field: 'username',
      headerName: t('teamPage.username'),
      flex: 1.1,
      minWidth: 180,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <span
            aria-hidden
            style={{
              width: 28,
              height: 28,
              borderRadius: 99,
              background: '#E6EAF6',
              color: '#00146D',
              display: 'grid',
              placeItems: 'center',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.02em',
              flexShrink: 0,
            }}
          >
            {initials(params.row.username)}
          </span>
          <span
            style={{
              fontWeight: 600,
              color: LIST_PAGE_COLORS.text,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {params.row.username}
          </span>
        </div>
      ),
    },
    {
      field: 'email',
      headerName: t('teamPage.email'),
      flex: 1.3,
      minWidth: 200,
      renderCell: (params) => (
        <ListPageDescription value={params.row.email} />
      ),
    },
    {
      field: 'role',
      headerName: t('teamPage.role'),
      flex: 0.8,
      minWidth: 140,
      sortable: false,
      renderCell: (params) => (
        <ListPagePill
          label={roleLabel(params.row.clientRoles?.[0])}
          bg="#EAF0F0"
          color="#5F7484"
          showDot={false}
          className="incyia-pill--body"
        />
      ),
    },
    {
      field: 'status',
      headerName: t('teamPage.status'),
      flex: 0.7,
      minWidth: 130,
      renderCell: (params) =>
        params.row.enabled ? (
          <ListPagePill
            label={t('teamPage.enabled')}
            bg="#E1F4F0"
            color="#076B5C"
            className="incyia-pill--body"
          />
        ) : (
          <ListPagePill
            label={t('teamPage.disabled')}
            bg="#FAEFDA"
            color="#84540A"
            className="incyia-pill--body"
          />
        ),
    },
    ...(isAdmin
      ? [
          {
            field: 'Action',
            headerName: t('teamPage.actions'),
            width: 130,
            flex: 0,
            sortable: false,
            headerAlign: 'right' as const,
            align: 'right' as const,
            renderCell: (params: GridCellParams) =>
              !params.row.enabled ? (
                <button
                  type="button"
                  onClick={() => handleEnableClick(params.row.username)}
                  style={{
                    height: 28,
                    padding: '0 12px',
                    borderRadius: 7,
                    border: '1px solid transparent',
                    background: LIST_PAGE_COLORS.tealSoft,
                    color: LIST_PAGE_COLORS.tealDeep,
                    fontSize: 13,
                    fontWeight: 600,
                    fontFamily: 'Lato, sans-serif',
                    cursor: 'pointer',
                  }}
                >
                  {t('teamPage.enable')}
                </button>
              ) : null,
          },
        ]
      : []),
  ]

  const chips: { key: StatusFilter; label: string }[] = [
    { key: 'all', label: t('teamPage.filterAll') },
    { key: 'enabled', label: t('teamPage.filterEnabled') },
    { key: 'disabled', label: t('teamPage.filterDisabled') },
  ]

  return (
    <>
      <UseAlert
        open={alert.open}
        message={alert.message}
        severity={alert.severity || 'info'}
        onClose={() => setAlert({ ...alert, open: false })}
      />

      <ListPageShell
        eyebrow={t('teamPage.eyebrow')}
        title={formatTitle(t('team'))}
        subtitle={t('teamPage.subtitle')}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder={t('teamPage.search')}
        showFiltersChip={false}
        countLabel={t('teamPage.membersCount', { count: rows.length })}
        chips={
          canManage ? (
            <>
              {chips.map((chip) => (
                <button
                  key={chip.key}
                  type="button"
                  style={listPageChipSx(statusFilter === chip.key)}
                  onClick={() => setStatusFilter(chip.key)}
                >
                  {chip.label}
                </button>
              ))}
            </>
          ) : undefined
        }
      >
        <div style={{ width: '100%' }}>
          <DataGrid
            loading={showLoader}
            rows={rows}
            columns={columns}
            hideFooterPagination
            hideFooter
            getRowId={(row) => row.id}
            getRowHeight={() => 56}
            disableColumnMenu
            disableColumnSelector
            disableRowSelectionOnClick
            slots={listPageGridSlots}
            sx={listPageDataGridSx(rows.length)}
          />
        </div>
      </ListPageShell>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false)
          setSelectedUsername(null)
        }}
        onConfirm={handleConfirmEnable}
        tone="default"
        title={t('teamPage.confirmEnableTitle')}
        message={t('teamPage.confirmEnableMessage', {
          username: selectedUsername || '',
        })}
        confirmText={t('teamPage.enable')}
        cancelText={t('buttons.cancel')}
        details={
          selected
            ? [
                {
                  label: t('teamPage.username'),
                  value: selected.username,
                },
                {
                  label: t('teamPage.email'),
                  value: 'email' in selected ? selected.email : '',
                },
                {
                  label: t('teamPage.role'),
                  value: roleLabel(
                    'clientRoles' in selected
                      ? selected.clientRoles?.[0]
                      : undefined
                  ),
                },
              ]
            : undefined
        }
      />
    </>
  )
}

export default TeamPage
